<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import { XIcon } from "~/icons";
import { NotificationType } from "~/shared/enums";
import type { INotification } from "~/shared/types";

import { copyAndNotify } from "./notification";

const NOTIFICATION_TYPE_TO_COLOR_MAP = {
  [NotificationType.INFO]: "bg-primary-base",
  [NotificationType.SUCCESS]: "bg-success-base",
  [NotificationType.WARNING]: "bg-warning-base",
  [NotificationType.ERROR]: "bg-danger-base",
};

const props = defineProps<{
  notification: INotification;
  dismiss: () => void;
  hovering: boolean;
}>();

const type = computed(() => props.notification.type ?? NotificationType.INFO);
const actions = computed(() => props.notification.actions ?? []);

// If forcibly dismissed, don't animate exit.
const forciblyDismissed = ref(false);
const onForceDismissal = () => {
  forciblyDismissed.value = true;
  nextTick(() => props.dismiss());
};

// Remove after timeout.
const delay = props.notification.duration ?? 10000;
// oxlint-disable-next-line dart/no-set-timeout
const setTimer = (time: number) => (props.notification.indefinite ? null : setTimeout(() => props.dismiss(), time));

let remainingTime = delay;
let timeout = setTimer(delay);
const timerEnd = new Date().getTime() + delay;

const copyContent = () => {
  if (
    props.notification.type === NotificationType.INFO ||
    props.notification.type === NotificationType.SUCCESS ||
    !props.notification.message
  ) {
    return;
  }
  copyAndNotify("Message", props.notification.message);
};

// Stop timer on hover.
watch(
  () => props.hovering,
  () => {
    if (props.hovering) {
      remainingTime = timerEnd - new Date().getTime();
      if (timeout) {
        clearTimeout(timeout);
      }
    } else {
      // Reset timer with remaining time.
      timeout = setTimer(remainingTime > 1000 ? remainingTime : 1000);
    }
  }
);

const color = NOTIFICATION_TYPE_TO_COLOR_MAP[type.value];
</script>

<template>
  <div
    class="flex w-full flex-col rounded-sm bg-std shadow-lg shadow-opposite/10"
    :class="[forciblyDismissed && 'transition-none']">
    <div class="h-2 rounded-t" :class="color" />
    <div class="flex flex-col gap-2 rounded-b border-x border-b border-md px-3 py-2">
      <div class="flex gap-2">
        <span
          class="w-full break-words flex-1 text-sm font-normal hyphens-auto text-md"
          @click="copyContent"
          @keydown.enter="copyContent">
          {{ props.notification.message }}
        </span>
        <button
          v-if="props.notification.isDismissible !== false"
          type="button"
          aria-label="Dismiss notification"
          class="shrink-0 cursor-pointer rounded-sm p-1 text-lt focus-ring-lt hover:bg-lt"
          @click="onForceDismissal()">
          <XIcon class="icon-sm" />
        </button>
      </div>
      <div v-if="actions.length" class="flex justify-end gap-1">
        <component
          :is="action.component ?? 'button'"
          v-bind="action.componentArgs"
          v-for="(action, index) in actions"
          :key="index"
          class="flex items-center rounded-sm border border-hvy bg-std px-2 py-1 text-center text-sm text-md shadow-xs focus-ring-lt select-none hover:bg-lt"
          @click="() => action.onClick?.(onForceDismissal)">
          {{ action.label }}
        </component>
      </div>
    </div>
  </div>
</template>
